import { tokens } from '@cast/design-system';

import { DateRangeUnsupportedBanner } from '../_components/DateRangeUnsupportedBanner';
import { StackedAreaChart } from '../_components/StackedAreaChart';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

import {
  GpuWasteChartPoint,
  useGpuWasteChartData,
} from './_hooks/useGpuWasteChartData';
import { isGpuDateRangeSupported } from './_utils/isGpuDateRangeSupported';

type GpuWasteChartMetricKey = 'onDemand' | 'fallback' | 'spot' | 'total';

const METRIC_SETTINGS: Record<
  GpuWasteChartMetricKey,
  { color?: string; title: string; tooltipTitle: string }
> = {
  total: {
    title: 'Total $ waste',
    tooltipTitle: 'Total $ waste',
  },
  onDemand: {
    color: tokens.theme.data.blue300,
    title: 'On-demand',
    tooltipTitle: 'On-demand',
  },
  fallback: {
    color: tokens.theme.data.yellow400,
    title: 'Fallback',
    tooltipTitle: 'Fallback',
  },
  spot: {
    color: tokens.theme.data.indigo800,
    title: 'Spot',
    tooltipTitle: 'Spot',
  },
};

const TITLE = 'GPU waste';
const TEST_ID = 'gpu-waste-chart';

export const GpuWasteChart = () => {
  const { timeZone, getRange } = useInventoryWorkloadsTimeRangeSelection();
  const { from, to } = getRange();
  const { data, totals, isLoading, error, refetch } = useGpuWasteChartData();

  if (!isGpuDateRangeSupported(from, to)) {
    return (
      <WidgetLayout title={TITLE} testId={TEST_ID}>
        <DateRangeUnsupportedBanner testId={`${TEST_ID}-unsupported-range`} />
      </WidgetLayout>
    );
  }

  return (
    <StackedAreaChart<GpuWasteChartPoint, GpuWasteChartMetricKey>
      title={TITLE}
      testId={TEST_ID}
      data={data}
      totals={totals}
      metricSettings={METRIC_SETTINGS}
      metricKeys={['total', 'onDemand', 'fallback', 'spot']}
      chartMetricKeys={['onDemand', 'fallback', 'spot']}
      timeZone={timeZone}
      unit=""
      valueType="currency"
      isLoading={isLoading}
      error={error}
      refetch={refetch}
    />
  );
};
